
import { Col, Row } from 'antd';
import Text from 'antd/lib/typography/Text';
import ZLSection from '../../containers/zl-section/zl-section';
import React, { useMemo } from 'react';
import { IPriceSet } from '../../interfaces/IPriceSet';
import { useSelector } from 'react-redux';
import useGlobalBookingAlert from '../../hooks/useGlobalBookingAlert';
import RichTextComponent from '../rich-text-component/rich-text-component';
import Link from 'antd/lib/typography/Link';
import { useTranslation } from 'react-i18next';
import Heading, { HeadingSize } from '../heading/heading';
import { useIsMobile } from '../../hooks/useIsMobile';
import { IApplicationState } from '../../store';


export interface IAlertItemProps {
    siteAlert?: string;
    priceSet?: IPriceSet;
}

function htmlToPlainText(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').trim();
}


export const BookingAlert: React.FunctionComponent<IAlertItemProps> = (props) => {

    const { t } = useTranslation();
    const { isMobile } = useIsMobile();

    const contentRef = React.useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = React.useState(false);

    const maxAlertLength = 800; //charsbefore alert is hidden
    const rolloverLength = isMobile ? 120 : 200; //chars before message is clipped behind a 'show more' label

    const alertTextLength = htmlToPlainText(props.siteAlert ?? '')?.length ?? 0;
    const alertNeedsClipping = alertTextLength > rolloverLength;

    const showVenueAlert = alertTextLength > 0 && alertTextLength <= maxAlertLength;
    const newsTitle = t('bookingDetails.titles.newsAndPromotions');
    const showMoreLabel = t('bookingDetails.labels.showMore');
    const showLessLabel = t('bookingDetails.labels.showLess');

    const venueAlertBox = useMemo(() => {
        return showVenueAlert ? (
            <>
                    <Text>
                        <div
                            ref={contentRef}
                            className={alertNeedsClipping && !expanded ? 'alert-clamp' : undefined}
                            dangerouslySetInnerHTML={{ __html: props.siteAlert ?? '' }} />
                    </Text>

                    {alertNeedsClipping && (
                        <Link onClick={() => setExpanded((prev) => !prev)}>
                            <strong>{expanded ? showLessLabel : showMoreLabel}</strong>
                        </Link>
                        )}
            </>   
        ) : <></>;
    }, [props.siteAlert, showVenueAlert, expanded, alertNeedsClipping, showMoreLabel, showLessLabel]);


    const selectedLanguage = useSelector((state: IApplicationState) => state.intl.selectedLanguage);
    //data loaded from contentful needs to match the locals we have there
    const { loading: gqlLoading, error: gqlError, liveData } = useGlobalBookingAlert(selectedLanguage.globalWebsiteLangCode ?? "en");

    const globalAlerts = liveData?.bookingAlertCollection?.items ?? [];

    // try to turn the contentful data into a valid list of global alerts to display
    const globalAlertsSection = !gqlLoading && !gqlError
        ? globalAlerts
            .filter(alert => {
                const json = alert?.alertText?.json;
                if (!json) return false;
                // check that JSON has real content
                const text = JSON.stringify(json).replace(/\s+/g, '');
                return text.length > 2; // {} is 2 chars, so >2 means something inside
            })
            ?.map((alert, index) => {
                return (
                    <Row key={alert.sys?.id ?? index} >
                        <Col>
                            <Text>
                                <RichTextComponent jsonData={alert?.alertText?.json} />
                            </Text>
                        </Col>
                    </Row>
                )
        })
        : undefined;


    const showGlobalAlert = globalAlertsSection !== undefined && globalAlertsSection.length > 0;
    // Price-set links (?set=) never show the banner.
    const showAnyAlert = (showGlobalAlert || showVenueAlert) && props.priceSet === undefined;

    return (
        <>
            {
                showAnyAlert &&
                <ZLSection active={true} extraClass='blue-alert' addStyleDividers={true} >
                        <Row align="middle" justify="start" className="alert-info">
                            <Col xs={24} sm={24} md={24} lg={24}>

                                {
                                    <Heading level={HeadingSize.Two} className="no-pad-heading" >
                                        <strong>{newsTitle.toUpperCase()}</strong>
                                    </Heading>
                                }

                                {showGlobalAlert && globalAlertsSection}

                                {showVenueAlert && venueAlertBox}

                                </Col>
                        </Row>
                    </ZLSection>
            }
        </>

        );
    };